import { useActionsTrack } from "@/_old/features/analytics";
import { useAuth } from "@/core/components/auth/store/auth";
import { useFilters } from "@/_old/features/filters";
import { cookieManager } from "@/_old/utils/cookie-manager";
import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { getUsersApi } from "../adapters/get-users";
import { UPDATE_PAGINATION } from "../constants/pagination";
import { MembersContextProps } from "./members-context.types";

const MembersContext = createContext<MembersContextProps>(
  {} as MembersContextProps,
);

export const MembersProvider: FunctionComponent<PropsWithChildren<{}>> = ({
  children,
}) => {
  const { groupId, groupName } = useFilters();
  const [search, setSearch] = useState("");
  const [membersSelected, setMembersSelected] = useState<
    { id: number; email: string; accessGroups?: number[] }[]
  >([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [totalPages, setTotalPages] = useState<number>(1);
  const [groupBatch, setGroupBatch] = useState<
    { value: string; label: string } | undefined
  >();
  const [PlanBatch, setPlanBatch] = useState<
    { value: string; label: string } | undefined
  >();
  const [roleBatch, setRoleBatch] = useState<
    { value: string; label: string } | undefined
  >();
  const [selectedEmailSubscribers, setSelectedEmailSubscribers] = useState<
    { id: number; email: string }[]
  >([]);
  const [selectedUserIds, setSelectedUserIds] = useState<
    { id: number; email: string }[]
  >([]);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  const [linesPerPage, setLinesPerPage] = useState<number>(10);
  const [activePage, setActivePage] = useState(
    searchParams.get("page") ? Number(searchParams.get("page")) : 1,
  );

  const {
    userInfo: { user, profile },
  } = useAuth();

  const { onActionClick } = useActionsTrack();

  const handleSelectbatch = (
    type: "group" | "plan" | "role",
    item:
      | {
          value: string;
          label: string;
        }
      | undefined,
  ) => {
    if (type === "group") {
      onActionClick("MEMBER_CHANGE_GROUP_BATCH", "COMPANY");
      setGroupBatch(item);
    } else if (type === "plan") {
      onActionClick("MEMBER_CHANGE_PLAN_BATCH", "COMPANY");
      setPlanBatch(item);
    } else if (type === "role") {
      onActionClick("MEMBER_CHANGE_ROLE_BATCH", "COMPANY", {
        roles: item?.value,
      });
      setRoleBatch(item);
    }
  };

  const page = Number(searchParams.get("page")) || 1;

  const usersFilters = useMemo(() => {
    return {
      limit: linesPerPage,
      page,
      league: Number(user.leagues_id[0]),
      search,
      groupId: groupId,
    };
  }, [linesPerPage, page, user.leagues_id, search, groupId]);

  useEffect(() => {
    const actualDate = new Date();
    const expirationDate = new Date(
      new Date(actualDate).setMonth(actualDate.getMonth() + 1),
    );

    cookieManager.set({
      cookieName: "linesPerPage",
      value: linesPerPage,
      globally: true,
      expirationDate,
    });
  }, [linesPerPage]);

  const handleChangeLinesPerPage = (value: number) => {
    setLinesPerPage(value);
  };

  useEffect(() => {
    refetchUsers();
  }, [usersFilters, profile.role]);

  useEffect(() => {
    if (total) {
      setTotalPages(Math.ceil(total / linesPerPage));
    }
  }, [total, linesPerPage]);

  const handleList = (page: number) => {
    setActivePage(page);
    setSearchParams({ page: String(page) });
  };

  const handleSelectMembers = (
    data: { id: number; email: string; accessGroups?: number[] }[],
  ) => {
    setMembersSelected(data);
  };

  const refetchUsers = async () => {
    if (!profile.permissions.includes("read-company-groups") && !groupId) {
      setData([]);
      setTotal(0);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getUsersApi(usersFilters);
      setData(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error("Error refetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = async (page: number) => {
    handleList(page);
  };

  const changeActivePage = (update: UPDATE_PAGINATION) => {
    const operation =
      update === UPDATE_PAGINATION.INCREMENT ? activePage + 1 : activePage - 1;
    handleChangePage(operation);
  };

  const decreasePage = () => {
    changeActivePage(UPDATE_PAGINATION.DECREMENT);
  };

  const increasePage = () => {
    changeActivePage(UPDATE_PAGINATION.INCREMENT);
  };

  const dataFormatted = useMemo(() => {
    return data?.map((item) => ({
      name: item?.user?.first_name,
      email: item?.profile?.email,
      role: item?.profile?.role?.length > 0 ? item?.profile?.role[0] : "",
      group: {
        id: item?.company_group?.id,
        user: item?.user?.id,
      },
      plan: { item: item?.plan, user: item?.user },
      user: item?.user,
      profile: item?.profile,
    }));
  }, [data]);

  return (
    <MembersContext.Provider
      value={{
        activePage,
        setActivePage,
        search,
        setSearch,
        data: dataFormatted,
        total,
        handleChangePage,
        groupId,
        page,
        handleChangeLinesPerPage,
        linesPerPage,
        membersSelected,
        setMembersSelected,
        handleSelectMembers,
        handleSelectbatch,
        PlanBatch,
        groupBatch,
        roleBatch,
        selectedEmailSubscribers,
        setSelectedEmailSubscribers,
        selectedUserIds,
        setSelectedUserIds,
        refetchUsers,
        isLoading,
      }}
    >
      {children}
    </MembersContext.Provider>
  );
};

export const useMembers = () => useContext(MembersContext);
